import AddIcon from "@mui/icons-material/Add"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import Column from "../../../common/components/Column"
import { podsApi } from "../axios"
import AdminPageHeader from "../components/AdminPageHeader"
import { MODE_LABELS } from "./podLabels"

export default function AdminPods() {
  const navigate = useNavigate()
  const [pods, setPods] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [createOpen, setCreateOpen] = useState(false)
  const [name, setName] = useState("")
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    podsApi
      .list()
      .then(({ data }) => setPods(data || []))
      .catch(() => setError("Could not load pods."))
      .finally(() => setIsLoading(false))
  }, [])

  function handleCreate() {
    setIsCreating(true)
    podsApi
      .create({ name: name.trim() })
      .then(({ data }) => navigate(`/admin/pods/${data.id}`))
      .catch(() => setError("Could not create the pod."))
      .finally(() => setIsCreating(false))
  }

  return (
    <Column gap={3}>
      <AdminPageHeader
        title="Pods"
        subtitle="Teams of texting-only agents dialing for client accounts on a schedule."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            New pod
          </Button>
        }
      />
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pod</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell align="right">Agents</TableCell>
              <TableCell align="right">Clients</TableCell>
              <TableCell align="right">Schedule blocks</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pods.map((pod) => (
              <TableRow
                key={pod.id}
                hover
                onClick={() => navigate(`/admin/pods/${pod.id}`)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell sx={{ fontWeight: 600 }}>{pod.name}</TableCell>
                <TableCell>{MODE_LABELS[pod.mode] || pod.mode}</TableCell>
                <TableCell align="right">
                  {pod.members.filter((m) => m.active).length}
                </TableCell>
                <TableCell align="right">
                  {pod.clients.filter((c) => c.active).length}
                </TableCell>
                <TableCell align="right">
                  {pod.schedule_blocks.filter((b) => b.active).length}
                </TableCell>
                <TableCell>{pod.active ? "Active" : "Off"}</TableCell>
              </TableRow>
            ))}
            {!isLoading && pods.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No pods yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New pod</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!name.trim() || isCreating}
            onClick={handleCreate}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Column>
  )
}
